import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { apiClient } from '../utils/api';
import {
  ALL_SHOPS_ID,
  CURRENT_SHOP_CHANGED_EVENT,
  isAllShopsSelection,
  readStoredCurrentShopId,
  readUserIdFromToken,
  writeStoredCurrentShopId,
} from '../utils/currentShop';

export interface ShopOption {
  id: string;
  name: string;
  isActive?: boolean;
}

interface CurrentShopContextValue {
  shops: ShopOption[];
  loading: boolean;
  currentShopId: string | null;
  defaultShopId: string | null;
  currentShop: ShopOption | null;
  isAllShopsMode: boolean;
  setCurrentShopId: (shopId: string) => void;
  refreshShops: () => Promise<void>;
}

const CurrentShopContext = createContext<CurrentShopContextValue | null>(null);

type BackofficeUserType = 'ADMIN' | 'PRACTITIONER' | 'EMPLOYEE';

function shopsEndpoint(userType: BackofficeUserType | undefined, role: string): string | null {
  if (role === 'super_admin') return '/api/admin/my-shops';

  switch (userType) {
    case 'ADMIN':
      return '/api/admin/my-shops';
    case 'EMPLOYEE':
      return '/api/employee/shops';
    case 'PRACTITIONER':
      return '/api/practitioner/shops';
    default:
      return null;
  }
}

function isKnownSelection(list: ShopOption[], shopId: string): boolean {
  if (isAllShopsSelection(shopId)) return list.length > 1;
  return list.some((s) => s.id === shopId);
}

/**
 * Shop resolution priority:
 * 1. Session selection (per browser tab) — supersedes DB default
 * 2. All shops, for roles that operate across the whole network (super admin)
 * 3. Role defaultShopId from API (persisted in DB)
 */
function resolveInitialShopId(
  list: ShopOption[],
  userId: string,
  roleDefaultShopId: string | null,
  defaultsToAllShops: boolean,
): string | null {
  if (!list.length || !userId) return null;

  const stored = readStoredCurrentShopId(userId);
  if (stored && isKnownSelection(list, stored)) return stored;

  if (defaultsToAllShops && list.length > 1) return ALL_SHOPS_ID;

  if (roleDefaultShopId && list.some((s) => s.id === roleDefaultShopId)) {
    return roleDefaultShopId;
  }

  if (list.length === 1) return list[0].id;

  return null;
}

export function CurrentShopProvider({ children }: { children: React.ReactNode }) {
  const user = useSelector((state: RootState) => state.auth.user);
  const userType = (user as { userType?: BackofficeUserType } | null)?.userType;
  const role = String(user?.role || '').toLowerCase().replace(/-/g, '_');
  const isSuperAdmin = role === 'super_admin';
  const endpoint = shopsEndpoint(userType, role);
  // The storage key must use the same identity as apiClient, otherwise the
  // selection is written under one key and read under another, and the
  // X-Current-Shop-Id header is never sent.
  const userId = readUserIdFromToken() ?? user?.id;
  const enabled = Boolean(user?.id && userId && endpoint);

  const [shops, setShops] = useState<ShopOption[]>([]);
  const [defaultShopId, setDefaultShopId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentShopId, setCurrentShopIdState] = useState<string | null>(null);

  const refreshShops = useCallback(async () => {
    if (!enabled || !endpoint || !userId) return;
    setLoading(true);
    try {
      const res = await apiClient(endpoint);
      if (!res.ok) return;
      const data = await res.json();
      const list: ShopOption[] = (data.data?.shops ?? []).map((s: ShopOption) => ({
        id: s.id,
        name: s.name,
        isActive: s.isActive,
      }));
      const apiDefault: string | null = data.data?.defaultShopId ?? null;
      setShops(list);
      setDefaultShopId(apiDefault);

      setCurrentShopIdState((prev) => {
        if (prev && isKnownSelection(list, prev)) return prev;
        const initial = resolveInitialShopId(list, userId, apiDefault, isSuperAdmin);
        if (initial) {
          writeStoredCurrentShopId(userId, initial);
        }
        return initial;
      });
    } finally {
      setLoading(false);
    }
  }, [enabled, endpoint, userId, isSuperAdmin]);

  useEffect(() => {
    if (!enabled) {
      setShops([]);
      setDefaultShopId(null);
      setCurrentShopIdState(null);
      return;
    }
    void refreshShops();
  }, [enabled, refreshShops]);

  useEffect(() => {
    if (!enabled || !userId) return;
    const stored = readStoredCurrentShopId(userId);
    if (stored) setCurrentShopIdState(stored);
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ shopId?: string; userId?: string }>).detail;
      if (detail?.userId && detail.userId !== userId) return;
      if (detail?.shopId) setCurrentShopIdState(detail.shopId);
    };
    window.addEventListener(CURRENT_SHOP_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CURRENT_SHOP_CHANGED_EVENT, onChanged);
  }, [enabled, userId]);

  const setCurrentShopId = useCallback(
    (shopId: string) => {
      if (!userId) return;
      if (isAllShopsSelection(shopId)) {
        if (shops.length <= 1) return;
        writeStoredCurrentShopId(userId, shopId);
        setCurrentShopIdState(shopId);
        return;
      }
      if (!shops.some((s) => s.id === shopId)) return;
      writeStoredCurrentShopId(userId, shopId);
      setCurrentShopIdState(shopId);
    },
    [shops, userId],
  );

  const isAllShopsMode = isAllShopsSelection(currentShopId);

  const currentShop = useMemo(() => {
    if (isAllShopsMode) return null;
    return shops.find((s) => s.id === currentShopId) ?? null;
  }, [shops, currentShopId, isAllShopsMode]);

  const value = useMemo<CurrentShopContextValue>(
    () => ({
      shops,
      loading,
      currentShopId,
      defaultShopId,
      currentShop,
      isAllShopsMode,
      setCurrentShopId,
      refreshShops,
    }),
    [
      shops,
      loading,
      currentShopId,
      defaultShopId,
      currentShop,
      isAllShopsMode,
      setCurrentShopId,
      refreshShops,
    ],
  );

  return <CurrentShopContext.Provider value={value}>{children}</CurrentShopContext.Provider>;
}

export function useCurrentShop(): CurrentShopContextValue {
  const ctx = useContext(CurrentShopContext);
  if (!ctx) {
    return {
      shops: [],
      loading: false,
      currentShopId: null,
      defaultShopId: null,
      currentShop: null,
      isAllShopsMode: false,
      setCurrentShopId: () => {},
      refreshShops: async () => {},
    };
  }
  return ctx;
}
